import api from './client';

export interface TaskLookupPowerPlant {
  id: number;
  name: string;
}

export interface TaskLookupCategory {
  id: number;
  description: string;
}

export interface TaskLookupUser {
  id: number;
  fullname: string;
}

export interface TaskCreateLookups {
  powerplants: TaskLookupPowerPlant[];
  taskcategories: TaskLookupCategory[];
  taskusers: TaskLookupUser[];
}

export interface CreateTaskPayload {
  powerplantId: number;
  taskcategoryId: number;
  taskassignees: number[];
  tasktitle: string;
  taskdescription: string;
  taskpriority: number;
  taskduedate?: string | null;
  relatedSourceUuidId?: string;
}

export interface CreateTaskResponse {
  ok: boolean;
  task: {
    id: number;
    uuid_id: string;
  };
}

export interface TaskListItem {
  id: number;
  uuid_id: string;
  task_category_id: number;
  title: string;
  power_plant_name: string;
  task_category_name: string;
  priority: number;
  description: string;
  task_status: number;
  task_created_date: string;
  task_due_date: string | null;
  assignees: TaskDetailAssignee[];
}

export async function fetchTasks(): Promise<TaskListItem[]> {
  const res = await api.get<TaskListItem[]>('/tasks');
  return res.data;
}

export interface TaskDetailAssignee {
  id: number;
  fullname: string;
}

export interface TaskDetailAttachment {
  id: number;
  file_name: string | null;
  path_name: string;
  mime_type: string | null;
}

export interface TaskDetail {
  id: number;
  uuid_id: string;
  power_plant_id: number;
  title: string;
  power_plant_name: string;
  task_category_id: number;
  task_category_name: string;
  priority: number;
  description: string;
  task_status: number;
  task_created_date: string;
  task_due_date: string | null;
  task_done_date: string | null;
  created_by_user: string | null;
  assignees: TaskDetailAssignee[];
  attachments: TaskDetailAttachment[];
}

export async function fetchTaskDetail(uuid_id: string): Promise<TaskDetail> {
  const res = await api.get<TaskDetail>(`/tasks/${uuid_id}`);
  return res.data;
}

export async function fetchTaskCreateLookups(): Promise<TaskCreateLookups> {
  const res = await api.get<TaskCreateLookups>('/tasks/lookups');
  return res.data;
}

export async function createTask(payload: CreateTaskPayload): Promise<CreateTaskResponse> {
  const res = await api.post<CreateTaskResponse>('/tasks', payload);
  return res.data;
}

// --- Task Board (Kanban) ---

export interface TaskBoardAssignee {
  id: number;
  fullname: string;
}

export interface TaskBoardItem {
  id: number;
  uuid_id: string;
  task_category_id: number;
  title: string;
  power_plant_name: string;
  task_category_name: string;
  priority: number;
  description: string;
  task_status: number;
  task_created_date: string;
  task_due_date: string | null;
  task_done_date: string | null;
  attachment_count: number;
  comment_count: number;
  assignees: TaskBoardAssignee[];
}

export interface TaskBoardComment {
  id: number;
  comment_text: string;
  created_on: string;
  user_id: number | null;
  user_name: string | null;
}

export interface TaskBoardHistoryItem {
  id: number;
  event_type: string;
  from_status: number | null;
  to_status: number | null;
  moved_by_id: number | null;
  moved_by_name: string | null;
  details: string | null;
  created_on: string;
}

export interface TaskBoardAttachment {
  id: number;
  file_name: string | null;
  path_name: string;
  mime_type: string | null;
  object_key: string | null;
  size_bytes: number;
  created_on: string;
  created_by_id: number | null;
  created_by_name: string | null;
}

export interface TaskBoardRelatedTask {
  id: number;
  uuid_id: string;
  title: string;
  description: string;
  task_status: number;
  priority: number;
  power_plant_name: string;
  task_category_name: string;
}

export interface TaskBoardDetail extends TaskDetail {
  comments: TaskBoardComment[];
  history: TaskBoardHistoryItem[];
  attachments: TaskBoardAttachment[];
  related_tasks: TaskBoardRelatedTask[];
}

export async function fetchTaskBoard(): Promise<TaskBoardItem[]> {
  const res = await api.get<TaskBoardItem[]>('/tasks/board');
  return res.data;
}

export async function fetchTaskBoardDetail(uuid_id: string): Promise<TaskBoardDetail> {
  const res = await api.get<TaskBoardDetail>(`/tasks/${uuid_id}/board`);
  return res.data;
}

export async function searchTasks(queryText: string, excludeUuid?: string): Promise<TaskBoardRelatedTask[]> {
  const res = await api.get<TaskBoardRelatedTask[]>('/tasks/search', {
    params: { q: queryText, excludeUuid },
  });
  return res.data;
}

export async function updateTask(uuid_id: string, payload: {
  taskStatus?: number;
  priority?: number;
  title?: string;
  description?: string;
  dueDate?: string | null;
  assignees?: number[];
}): Promise<void> {
  await api.patch(`/tasks/${uuid_id}`, payload);
}

export async function deleteTask(uuid_id: string): Promise<void> {
  await api.delete(`/tasks/${uuid_id}`);
}

export async function addTaskComment(uuid_id: string, comment: string): Promise<TaskBoardComment> {
  const res = await api.post<TaskBoardComment>(`/tasks/${uuid_id}/comments`, { comment });
  return res.data;
}

export async function addTaskAttachment(uuid_id: string, file: File): Promise<TaskBoardAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<TaskBoardAttachment>(`/tasks/${uuid_id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function deleteTaskAttachment(uuid_id: string, attachmentId: number): Promise<void> {
  await api.delete(`/tasks/${uuid_id}/attachments/${attachmentId}`);
}

export async function addTaskRelation(uuid_id: string, relatedUuidId: string): Promise<void> {
  await api.post(`/tasks/${uuid_id}/relations`, { relatedUuidId });
}

export async function deleteTaskRelation(uuid_id: string, relatedUuidId: string): Promise<void> {
  await api.delete(`/tasks/${uuid_id}/relations/${relatedUuidId}`);
}
